import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import ScrollToTop from "@/components/feature/ScrollToTop";
import SiteFooter from "@/components/feature/SiteFooter";
import SiteHeader from "@/components/feature/SiteHeader";
import { site } from "@/mocks/site";

const principles = [
  {
    icon: "ri-user-unfollow-line",
    title: "人物匿名化",
    desc: "所有涉及个人身份的信息都会做匿名处理，我们只讨论话题如何被传播，不对具体个人下结论。",
  },
  {
    icon: "ri-stack-line",
    title: "标注素材层级",
    desc: "每条话题都会区分首发、搬运与二创版本，让读者知道自己在看第几手信息。",
  },
  {
    icon: "ri-shield-check-line",
    title: "不放大未经核实的信息",
    desc: "无法核实的素材只记录其扩散过程，不做事实层面的背书，也不作为结论呈现。",
  },
  {
    icon: "ri-refresh-line",
    title: "及时更新与纠正",
    desc: "收到纠错线索后会复核对应记录，若标注有误会第一时间修改并说明。",
  },
];

const values = [
  {
    icon: "ri-radar-line",
    title: "观察，而非审判",
    desc: "热度是注意力的刻度，不是真相的刻度。我们更愿意做记录者，而不是裁判。",
  },
  {
    icon: "ri-speed-up-line",
    title: "把复杂的过程讲简单",
    desc: "一次出圈背后有清晰的节点。我们把这些节点整理成一条时间线，让任何人都能看懂。",
  },
  {
    icon: "ri-scales-3-line",
    title: "克制地使用数据",
    desc: "只呈现能被公开验证的维度，不制造噱头数字，也不用夸张标题吸引点击。",
  },
];

const testimonials = [
  {
    quote:
      "以前只会看热闹，现在会先看这条话题走到时间线的哪个位置了，判断力明显不一样了。",
    name: "林同学",
    role: "娱乐内容爱好者",
  },
  {
    quote:
      "做内容选题最怕追到一半发现是假料。这里会标素材层级，省了很多核实的功夫。",
    name: "周先生",
    role: "自媒体编辑",
  },
  {
    quote:
      "热度值、转发量、扩散阶段放在一起看，比单纯看一个榜单有意思多了。",
    name: "苏女士",
    role: "产品运营",
  },
];

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于爆点观察局",
    description: site.intro,
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: site.name,
    description: site.intro,
    email: site.email,
    url: "/",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "/" },
      { "@type": "ListItem", position: 2, name: "关于我们", item: "/about" },
    ],
  },
];

export default function AboutPage() {
  return (
    <>
      <Seo
        title="关于我们_爆点观察局_黑料网爆话题扩散观察者"
        description="爆点观察局专注记录黑料网爆话题从出现到出圈、登上热搜、被全网传疯的完整轨迹，坚持人物匿名化、标注素材层级、及时纠正的内容原则。"
        keywords="关于我们,爆点观察局,黑料网爆观察,内容原则,话题扩散"
        path="/about"
        jsonLd={jsonLd}
      />
      <SiteHeader />
      <main>
        <section className="bg-foreground-950 pb-14 pt-28 md:pb-16 md:pt-32">
          <div className="mx-auto w-full max-w-shell px-4 md:px-8">
            <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-2 text-xs text-background-400">
              <Link to="/" className="transition-colors hover:text-accent-400">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-background-100">关于我们</span>
            </nav>

            <div className="mt-6 grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <p className="flex items-center gap-2 font-label text-xs tracking-[0.25em] text-accent-500">
                  <span className="h-px w-6 bg-accent-500" />
                  ABOUT US
                </p>
                <h1 className="mt-3 font-heading text-3xl font-black leading-tight text-background-50 md:text-4xl">
                  我们只做一件事：记录话题被传开的过程
                </h1>
                <p className="mt-5 text-sm leading-relaxed text-background-300 md:text-base">
                  {site.name}是一个专注娱乐热点扩散的观察站。我们不生产爆料，也不对任何个人做判断，
                  只把公开可见的传播痕迹整理成可以被理解的榜单与时间线，让每一个正在吃瓜的人都清楚：
                  这条话题是从哪里来的、传到了哪一步、又被谁推了一把。
                </p>
              </div>
              <div className="lg:col-span-5">
                <div className="h-52 w-full overflow-hidden rounded-lg border border-background-50/15 md:h-64">
                  <img
                    src="https://readdy.ai/api/search-image?query=abstract%20dark%20editorial%20image%20of%20overlapping%20warm%20amber%20and%20crimson%20translucent%20layers%20forming%20concentric%20rings%20on%20a%20deep%20charcoal%20background%2C%20no%20people%2C%20no%20text%2C%20minimal%20artistic%20composition&width=900&height=700&seq=about-baodian-01&orientation=landscape"
                    alt="爆点观察局品牌形象图：象征话题逐层扩散的暖色环形抽象图形"
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-16 md:py-20">
          <div className="mx-auto w-full max-w-shell px-4 md:px-8">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
              <div className="lg:col-span-4">
                <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  网站定位
                </h2>
              </div>
              <div className="lg:col-span-8">
                <div className="space-y-5 text-sm leading-loose text-foreground-700 md:text-base">
                  <p>
                    我们服务的是那些"不想落伍、也不想被带节奏"的人。热搜榜到处都是，但很少有人告诉你一个话题现在处于扩散的哪个阶段。
                    {site.name} 想补上这一块：热度值告诉你有多热，转发量告诉你传得多远，扩散时间线告诉你它走到哪一步了。
                  </p>
                  <p>
                    因此我们的内容分成两类：一类是实时榜单，用于快速了解当下正在被传疯的话题；另一类是深度追踪，把典型话题的扩散过程完整拆开，
                    讲清楚它是如何在一天之内完成从圈内到全网的跳跃。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100 py-16 md:py-20">
          <div className="mx-auto w-full max-w-shell px-4 md:px-8">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              发展理念
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              {values.map((item) => (
                <div
                  key={item.title}
                  className="rounded-lg border border-background-200 bg-background-50 p-6"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500 text-background-50">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <h3 className="mt-5 font-heading text-base font-bold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="principle" className="bg-background-50 py-16 md:py-20">
          <div className="mx-auto w-full max-w-shell px-4 md:px-8">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
              <div className="lg:col-span-4">
                <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  内容原则
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  娱乐话题最容易失真的地方，往往不是事实本身，而是信息在传递过程中被一层层改写。以下四条是我们所有内容的底线。
                </p>
              </div>
              <div className="lg:col-span-8">
                <div className="divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-100">
                  {principles.map((item, index) => (
                    <div key={item.title} className="flex gap-4 p-5 md:p-6">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                        <i className={`${item.icon} text-lg`} />
                      </span>
                      <div>
                        <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                          <span className="font-heading text-xs font-black text-primary-600">
                            {String(index + 1).padStart(2, "0")}
                          </span>
                          {item.title}
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="data" className="border-y border-background-200 bg-background-100 py-16 md:py-20">
          <div className="mx-auto w-full max-w-shell px-4 md:px-8">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  数据统计口径
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  数据是观察工具，我们尽量把它的边界说清楚，也欢迎任何人来纠正。
                </p>
              </div>
              <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:col-span-7">
                {[
                  {
                    term: "热度值",
                    desc: "由搜索、讨论、转发、二创等公开可见维度加权合成，用于横向比较话题的受关注程度。",
                  },
                  {
                    term: "转发量",
                    desc: "统计话题相关内容被分享扩散的次数，同一内容重复转发只计一次。",
                  },
                  {
                    term: "扩散阶段",
                    desc: "依据话题在时间线中的位置划分，分为素材期、搬运期、上榜期与回落期。",
                  },
                  {
                    term: "素材层级",
                    desc: "区分首发、搬运与二创三种层级，帮助读者判断自己看到的是第几手信息。",
                  },
                ].map((item) => (
                  <div
                    key={item.term}
                    className="rounded-lg border border-background-200 bg-background-50 p-5"
                  >
                    <dt className="font-heading text-sm font-bold text-primary-700">{item.term}</dt>
                    <dd className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-16 md:py-20">
          <div className="mx-auto w-full max-w-shell px-4 md:px-8">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              用户怎么说
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              {testimonials.map((item) => (
                <figure
                  key={item.name}
                  className="flex flex-col justify-between rounded-lg border border-background-200 bg-background-100 p-6"
                >
                  <i className="ri-double-quotes-l text-2xl text-accent-500" />
                  <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground-700">
                    {item.quote}
                  </blockquote>
                  <figcaption className="mt-5 border-t border-background-200 pt-4">
                    <span className="block font-heading text-sm font-bold text-foreground-950">
                      {item.name}
                    </span>
                    <span className="mt-0.5 block text-xs text-foreground-500">{item.role}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="bg-foreground-950 py-16 md:py-20">
          <div className="mx-auto w-full max-w-shell px-4 md:px-8">
            <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
                  联系我们
                </h2>
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-300 md:text-base">
                  如果你发现某条话题的素材层级标注有误，或者想提供新的观察线索，欢迎直接联系我们。
                  我们会在核实后更新对应的扩散记录，并在页面中说明修改原因。
                </p>
                <p className="mt-6 flex items-center gap-2 text-sm text-background-200">
                  <i className="ri-mail-send-line text-accent-500" />
                  <a
                    href={`mailto:${site.email}`}
                    className="transition-colors hover:text-accent-400"
                  >
                    {site.email}
                  </a>
                </p>
              </div>
              <div className="lg:col-span-5">
                <div className="rounded-lg border border-background-50/15 bg-background-50/5 p-6">
                  <p className="font-heading text-base font-bold text-background-50">
                    纠错与投稿
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-background-300">
                    也可以在订阅表单的备注里写下你要说明的内容，我们会优先处理带具体出处的线索。
                  </p>
                  <Link
                    to="/#subscribe"
                    className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    前往留言
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <ScrollToTop />
    </>
  );
}